import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Crown } from "lucide-react";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") || "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null); // {valid, premium_months, lifetime, label}

  useEffect(() => {
    if (user && typeof user === "object") navigate("/profile", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    let active = true;
    if (inviteToken) {
      api.get(`/auth/premium-invite/${inviteToken}`)
        .then(({ data }) => { if (active) setInvite(data); })
        .catch(() => { if (active) setInvite({ valid: false }); });
    }
    return () => { active = false; };
  }, [inviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register(email.trim().toLowerCase(), password, name.trim(), inviteToken || undefined);
      navigate("/profile");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const premiumDuration = invite?.valid
    ? (invite.lifetime ? "livstid" : `${invite.premium_months} måneders`)
    : null;

  return (
    <div className="max-w-md mx-auto px-4 py-16 animate-fade-up" data-testid="register-page">
      <div className="eyebrow">Start din rejse</div>
      <h1 className="mt-3 font-serif text-4xl text-olive-700">Opret din konto</h1>
      <p className="mt-3 text-brown-600 text-sm">
        Derefter opbygger du en privat profil, som vi gennemgår før den bliver aktiv.
      </p>

      {invite?.valid && (
        <div className="mt-6 rounded-2xl border border-gold-500/40 bg-gradient-to-br from-gold-100/70 to-sand-100 p-4 flex items-start gap-3" data-testid="invite-premium-banner">
          <div className="h-10 w-10 rounded-full bg-gold-500/20 text-gold-700 flex items-center justify-center shrink-0">
            <Crown className="h-5 w-5" />
          </div>
          <div>
            <div className="font-serif text-lg text-olive-700">Premium-invitation</div>
            <p className="text-sm text-brown-600 mt-0.5">
              Du er inviteret! Din konto får automatisk <strong>{premiumDuration} Premium</strong> når du opretter dig.
            </p>
          </div>
        </div>
      )}
      {invite && invite.valid === false && (
        <div className="mt-6 rounded-2xl border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive" data-testid="invite-invalid-banner">
          Dette invitationslink er ugyldigt eller allerede brugt. Du kan stadig oprette en almindelig konto.
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="block text-sm mb-1 text-brown-600">Dit navn</label>
          <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} className="field" placeholder="Fuldt navn" />
        </div>
        <div>
          <label className="block text-sm mb-1 text-brown-600">E-mail</label>
          <input data-testid="register-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="field" placeholder="dig@eksempel.dk" />
        </div>
        <div>
          <label className="block text-sm mb-1 text-brown-600">Adgangskode</label>
          <input data-testid="register-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="field" placeholder="Mindst 8 tegn" />
        </div>
        {err && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-2" data-testid="register-error">{err}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="register-submit">
          {busy ? "Opretter…" : "Fortsæt"}
        </button>
      </form>

      <div className="mt-6 text-sm text-brown-500">
        Har du allerede en konto?{" "}
        <Link to="/login" className="text-olive-600 hover:underline" data-testid="login-link-from-register">
          Log ind
        </Link>
      </div>
    </div>
  );
}
